/**
 * Public RPC Client - SECURITY DEFINER Functions Only
 * 
 * All public invite pages MUST use these functions.
 * Direct Supabase table access is FORBIDDEN for public routes.
 */

import { supabase } from '../supabaseClient';

/**
 * Fetch invite details by token
 * RPC: public_get_invite(token)
 * 
 * @param {string} token - Invite token from URL
 * @returns {Promise<{person_name, person_email, person_company, person_phone, status, expires_at, brief_title} | null>}
 */
export async function getInvite(token) {
  const { data, error } = await supabase.rpc('public_get_invite', {
    p_token: token
  });

  if (error) {
    console.error('getInvite RPC error:', error);
    throw new Error(error.message);
  }

  // RPC returns array, take first result
  return data && data.length > 0 ? data[0] : null;
}

/**
 * Verify 6-digit code for invite
 * RPC: public_verify_code(token, code)
 * 
 * @param {string} token - Invite token
 * @param {string} code - 6-digit verification code
 * @returns {Promise<boolean>}
 */
export async function verifyCode(token, code) {
  const { data, error } = await supabase.rpc('public_verify_code', {
    p_token: token,
    p_code: code
  });

  if (error) {
    console.error('verifyCode RPC error:', error);
    throw new Error(error.message);
  }

  return data === true;
}

/**
 * Fetch induction brief content by token
 * RPC: public_get_brief(token)
 * 
 * @param {string} token - Invite token
 * @returns {Promise<{title, content} | null>}
 */
export async function getBrief(token) {
  const { data, error } = await supabase.rpc('public_get_brief', {
    p_token: token
  });

  if (error) {
    console.error('getBrief RPC error:', error);
    throw new Error(error.message);
  }

  return data && data.length > 0 ? data[0] : null;
}

/**
 * Submit completed induction
 * RPC: public_submit_induction(token, answers, signature)
 * 
 * @param {string} token - Invite token
 * @param {object} answers - JSONB answers object
 * @param {string} signatureDataUrl - Base64 signature image
 * @returns {Promise<{success, status, submitted_at}>}
 */
export async function submitInduction(token, answers, signatureDataUrl) {
  const { data, error } = await supabase.rpc('public_submit_induction', {
    p_token: token,
    p_answers: answers,
    p_signature_data_url: signatureDataUrl
  });

  if (error) {
    console.error('submitInduction RPC error:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Fetch approved pass details by invite token
 * RPC: public_get_approved_pass(token)
 *
 * Requires the SQL function in:
 *   add_public_get_approved_pass.sql
 *
 * Returns: { person_name, person_email, person_company, site_name,
 *             brief_title, pass_token, approved_at, valid_until, status }
 */
export async function getApprovedPass(token) {
  const { data, error } = await supabase.rpc('public_get_approved_pass', {
    p_token: token,
  });

  if (error) {
    // Graceful degradation — the approval page still works without pass details
    console.warn('getApprovedPass RPC not available or errored:', error.message);
    return null;
  }

  return data && data.length > 0 ? data[0] : data ?? null;
}

/**
 * Parse RPC error messages for user-friendly display
 * 
 * @param {Error} error - Error from RPC call
 * @returns {string} User-friendly error message
 */
export function parseRpcError(error) {
  const message = error.message || error.toString();

  // Handle common error patterns
  if (message.includes('locked')) {
    return 'This invite is temporarily locked due to too many verification attempts. Please try again later.';
  }

  if (message.includes('expired')) {
    return 'This invite has expired. Please contact your site administrator for a new invite.';
  }

  if (message.includes('Invalid') || message.includes('invalid')) {
    return 'Invalid invite link. Please check your email for the correct link.';
  }

  if (message.includes('already')) {
    return 'This induction has already been submitted.';
  }

  // Generic fallback
  return 'An error occurred. Please try again or contact support.';
}
